import React, { useState, useContext, useEffect } from "react"
import Header from "../components/Header"
import { IonLabel, IonPage, IonContent, IonSegment, IonSegmentButton, IonList } from "@ionic/react"
import TicketListItem from "../components/TicketListItem"
import { DatabaseContext } from "../components/DatabaseContext"
import { useTranslation } from "react-i18next"
import Loading from "../components/Loading"
import { UserContext } from "../components/UserContext"

const Tickets = (props) => {
	const { t } = useTranslation("link_app")
	const [segment, setSegment] = useState("open")
	const { match } = props

	// Get user data
	const [user] = useContext(UserContext)

	// Databases
	const databases = useContext(DatabaseContext)

	// Fetching state
	const [isFetching, setIsFetching] = useState(true)

	// Subscribe to a PouchDB collection: initial allDocs + live changes feed.
	// Filter is applied to both the bulk load and incoming changes so the list
	// stays consistent regardless of where rows enter or leave.
	const subscribeCollection = (db, filter, setRows, onInitialLoad) => {
		let cancelled = false
		const rowsById = new Map()

		const matches = (doc) => !!doc && !doc._id.includes("_design") && filter(doc)

		const emit = () => {
			if (cancelled) return
			setRows(Array.from(rowsById.values()))
		}

		db.allDocs({ include_docs: true })
			.then((result) => {
				if (cancelled) return
				for (const row of result.rows) {
					if (matches(row.doc)) {
						rowsById.set(row.id, { id: row.id, doc: row.doc })
					}
				}
				emit()
				if (onInitialLoad) onInitialLoad()
			})
			.catch((err) => {
				console.log(err)
				if (onInitialLoad) onInitialLoad()
			})

		const changes = db
			.changes({ live: true, since: "now", include_docs: true })
			.on("change", (change) => {
				if (cancelled) return
				if (change.deleted || !matches(change.doc)) {
					if (rowsById.delete(change.id)) emit()
				} else {
					rowsById.set(change.id, { id: change.id, doc: change.doc })
					emit()
				}
			})
			.on("error", (err) => {
				console.log(err)
			})

		return () => {
			cancelled = true
			changes.cancel()
		}
	}

	// Fetch tickets
	const [tickets, setTickets] = useState([])
	useEffect(() => {
		if (!user?.name) return
		const ownedBy = user.name.replace("temp-", "")
		return subscribeCollection(
			databases.locals.tickets,
			(doc) => doc.resourced?.findIndex((contact) => contact.id === ownedBy) > -1,
			setTickets,
			() => setIsFetching(false)
		)
	}, [databases.locals.tickets, match, user])

	// Fetch ticket history
	const [ticketHistory, setTicketHistory] = useState([])
	useEffect(() => {
		if (!user?.name) return
		return subscribeCollection(databases.locals.ticket_history, (doc) => doc.resourced?.findIndex((contact) => contact.id === user.name) > -1, setTicketHistory)
	}, [databases.locals.ticket_history, match, user])

	useEffect(() => {
		document.title = `${t("app.ticket_list")} ${window.location.host.includes("localhost") || window.location.host.includes("staging") ? "[STAGING] " : ""}- Link App - Telemast`
	}, [t, match])

	return (
		<IonPage>
			<Header title={t("app.ticket_list")} />
			<IonContent>
				{!isFetching ? (
					<>
						<IonSegment
							onIonChange={(e) => {
								e.preventDefault()
								setSegment(e.detail.value)
							}}
							value={segment}
						>
							<IonSegmentButton value="open">
								<IonLabel>
									{t("tickets.open")} ({tickets.length})
								</IonLabel>
							</IonSegmentButton>
							<IonSegmentButton value="history">
								<IonLabel>
									{t("tickets.history")} ({ticketHistory.length})
								</IonLabel>
							</IonSegmentButton>
						</IonSegment>
						<IonList>
							{segment === "open" && (
								<>
									{tickets
										.sort((a, b) => {
											return parseInt(b.id) - parseInt(a.id)
										})
										.map((item, i) => (
											<TicketListItem key={i} item={item} />
										))}
								</>
							)}
							{segment === "history" && (
								<>
									{ticketHistory
										.sort((a, b) => {
											return parseInt(b.id) - parseInt(a.id)
										})
										.map((item, i) => (
											<TicketListItem historyItem key={i} item={item} />
										))}
								</>
							)}
						</IonList>
					</>
				) : (
					<Loading />
				)}
			</IonContent>
		</IonPage>
	)
}

export default Tickets
